export type MapTravelMode = 'drive' | 'walk' | 'transit';

const toGoogleTravelMode = (mode: MapTravelMode): string => {
  switch (mode) {
    case 'walk':
      return 'WALKING';
    case 'transit':
      return 'TRANSIT';
    default:
      return 'DRIVING';
  }
};

export const normalizeMapTravelMode = (mode?: string | null): MapTravelMode => {
  if (mode === 'walk' || mode === 'transit') return mode;
  return 'drive';
};

export const getLegTravelMode = (
  leg?: { mode?: string; chosenMode?: string } | null,
  fallback: MapTravelMode = 'drive',
): MapTravelMode => normalizeMapTravelMode(leg?.chosenMode || leg?.mode || fallback);

// Session cache: the overview path for a (from, to, mode) triple is stable enough
// for display, so route it once and reuse across every re-plot instead of calling
// DirectionsService on each render. Caches the promise (dedupes concurrent
// callers); a null result is evicted so a later attempt can retry.
const directionsCache = new Map<string, Promise<Array<{ lat: number; lng: number }> | null>>();
const coordKey = (p: { lat: number; lng: number }) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;

export const fetchDirectionsPath = (
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number },
  mode: MapTravelMode,
): Promise<Array<{ lat: number; lng: number }> | null> => {
  if (!window.google?.maps?.DirectionsService) {
    return Promise.resolve(null);
  }

  const cacheKey = `${coordKey(origin)}|${coordKey(destination)}|${mode}`;
  const cached = directionsCache.get(cacheKey);
  if (cached) return cached;

  const travelMode = window.google.maps.TravelMode?.[toGoogleTravelMode(mode)]
    || toGoogleTravelMode(mode);

  const pending = new Promise<Array<{ lat: number; lng: number }> | null>((resolve) => {
    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin,
        destination,
        travelMode,
      },
      (result: { routes?: Array<{ overview_path?: Array<{ lat: () => number; lng: () => number }> }> } | null, status: string) => {
        const path = result?.routes?.[0]?.overview_path;
        if (status !== 'OK' || !path?.length) {
          resolve(null);
          return;
        }

        resolve(path.map((point) => ({ lat: point.lat(), lng: point.lng() })));
      },
    );
  });

  directionsCache.set(cacheKey, pending);
  pending.then((res) => { if (!res) directionsCache.delete(cacheKey); }).catch(() => directionsCache.delete(cacheKey));
  return pending;
};
